import { api } from '@/config/api';

export interface CreateFbCampaignPayload {
  expected_account_uid?: string;
  name: string;
  objective: 'OUTCOME_AWARENESS' | 'OUTCOME_TRAFFIC' | 'OUTCOME_ENGAGEMENT' | 'OUTCOME_LEADS' | 'OUTCOME_APP_PROMOTION' | 'OUTCOME_SALES';
  status?: 'ACTIVE' | 'PAUSED';
  special_ad_categories?: string[];
  buying_type?: 'AUCTION' | 'RESERVED';
  daily_budget?: string;       // minor units
  lifetime_budget?: string;
  spend_cap?: string;          // minor units, optional lifetime hard cap
  start_time?: string;
  stop_time?: string;
}

export interface UpdateFbCampaignPayload {
  name?: string;
  status?: 'ACTIVE' | 'PAUSED' | 'ARCHIVED';
  daily_budget?: string;
  lifetime_budget?: string;
  start_time?: string;
  stop_time?: string;
}

export interface FbInsightsRow {
  impressions?: string;
  clicks?: string;
  spend?: string;
  ctr?: string;
  cpc?: string;
  cpm?: string;
  reach?: string;
  frequency?: string;
  actions?: Array<{ action_type: string; value: string }>;
}

export interface FbCampaign {
  id: string;
  name: string;
  status: string;
  effective_status: string;
  objective: string;
  daily_budget?: string;
  lifetime_budget?: string;
  budget_remaining?: string;
  start_time?: string;
  stop_time?: string;
  created_time?: string;
  updated_time?: string;
  /** Derived from local ad-set targeting. e.g. ['facebook','instagram'] */
  platforms?: string[];
  /** Derived from the campaign's first ad-set's promoted_object.page_id. */
  pageId?: string;
  insights?: { data: FbInsightsRow[] };
}

export interface FbCampaignsListResponse {
  data: FbCampaign[];
  paging?: { cursors: { before?: string; after?: string }; next?: string };
  fetchedAt: string;
  fromCache: boolean;
}

export const facebookCampaignsService = {
  list: async (
    refresh = false,
    limit?: number,
    range?: { datePreset?: string; since?: string; until?: string },
  ): Promise<FbCampaignsListResponse> =>
    (await api.get('/slighter_crm/facebook/campaigns', {
      params: {
        refresh: refresh ? '1' : undefined,
        limit,
        datePreset: range?.datePreset,
        since: range?.since,
        until: range?.until,
      },
    })).data,
  get: async (campaignId: string, refresh = false): Promise<FbCampaign> =>
    (await api.get(`/slighter_crm/facebook/campaigns/${campaignId}`, { params: { refresh: refresh ? '1' : undefined } })).data,
  create: async (payload: CreateFbCampaignPayload): Promise<{ id: string }> =>
    (await api.post('/slighter_crm/facebook/campaigns', payload)).data,
  update: async (campaignId: string, payload: UpdateFbCampaignPayload): Promise<{ success: boolean }> =>
    (await api.patch(`/slighter_crm/facebook/campaigns/${campaignId}`, payload)).data,
  pause: async (campaignId: string) =>
    (await api.post(`/slighter_crm/facebook/campaigns/${campaignId}/pause`)).data,
  resume: async (campaignId: string) =>
    (await api.post(`/slighter_crm/facebook/campaigns/${campaignId}/resume`)).data,
  archive: async (campaignId: string) =>
    (await api.post(`/slighter_crm/facebook/campaigns/${campaignId}/archive`)).data,
  remove: async (campaignId: string): Promise<{ success: boolean }> =>
    (await api.delete(`/slighter_crm/facebook/campaigns/${campaignId}`)).data,
};

/** Helper to extract the first row of insights data (it's wrapped in `{ data: [...] }`). */
export function getInsights(c: FbCampaign): FbInsightsRow | null {
  return c.insights?.data?.[0] ?? null;
}
